'use client'

import { useEffect, useRef, useState } from 'react'
import type { NavigationItemFragment } from '@/lib/types/datocms/graphql/generated'
import Cta from '@/components/ui/cta'
import Icon from '@/components/svg/icon'
import useClickOutside from '@/hooks/useClickOutside'
import { useIsResizing } from '@/hooks/useIsResizing'
import clsx from 'clsx'

type Props = {
	dropdownNavigation?: NavigationItemFragment
	dropdownOptions: NavigationItemFragment[]
	setShowMobileMenu?: (value: boolean) => void
	title?: string | null
	currentDropdown: string | null
	setCurrentDropdown: (value: string | null) => void
	setActiveMegaMenu?: (value: string | null | undefined) => void
	setIsMegaMenuOpen: (value: boolean) => void
}

export default function NavItemDropdown({
	dropdownNavigation,
	dropdownOptions,
	setShowMobileMenu,
	title,
	currentDropdown,
	setCurrentDropdown,
	setActiveMegaMenu,
	setIsMegaMenuOpen,
}: Readonly<Props>) {
	const menuRef = useRef<HTMLDivElement | null>(null)
	const isOpen = currentDropdown === title
	const isResizing = useIsResizing()
	const [lastScrollY, setLastScrollY] = useState(0)

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY

			// If scrolling down and mega menu is open, close it
			if (currentScrollY > lastScrollY && isOpen) {
				setCurrentDropdown('')
			}

			setLastScrollY(currentScrollY)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [lastScrollY, isOpen, setCurrentDropdown])

	useClickOutside(
		menuRef,
		() => {
			setCurrentDropdown('')
		},
		'a[href], button'
	)

	const handleOpenDropdown = () => {
		if (!isOpen) {
			setCurrentDropdown(title ?? '')
			setActiveMegaMenu?.('')
			setIsMegaMenuOpen(false)
		} else {
			setCurrentDropdown('')
		}
	}

	return (
		<div ref={menuRef} className={clsx('relative max-lg:-z-10', isOpen && 'max-lg:border-b')}>
			<button
				className={clsx(
					'flex items-center max-lg:justify-between gap-1 text-left px-3 lg:px-2 py-2 max-lg:font-semibold max-lg:w-full max-lg:py-5 max-lg:border-b max-lg:hover:bg-white-frosted',
					!isResizing && 'transition-colors duration-300'
				)}
				onMouseDown={handleOpenDropdown}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleOpenDropdown()
					}
				}}
			>
				{dropdownNavigation ? (
					<Cta cta={dropdownNavigation}>{dropdownNavigation.label}</Cta>
				) : (
					title
				)}
				<Icon
					icon="Chevron"
					className={clsx(
						'text-blue max-lg:w-4 w-3',
						!isResizing && 'transition-transform duration-300',
						isOpen ? 'rotate-90 lg:-rotate-90' : 'rotate-0 lg:rotate-90'
					)}
				/>
			</button>

			<div
				className={clsx(
					'lg:absolute flex flex-col top-full bg-white rounded-b-md overflow-hidden lg:-ml-4 lg:mt-6 -z-50',
					!isResizing && 'transition-transform m-ease duration-200',
					isOpen ? 'lg:-translate-y-2 lg:shadow-md' : 'lg:-translate-y-[calc(100%+8px)]',
					' max-lg:grid max-lg:transition-all max-lg:duration-300 max-lg:ease-in-out',
					isOpen ? 'max-lg:grid-rows-[1fr]' : 'max-lg:grid-rows-[0fr]'
				)}
			>
				<div className="overflow-hidden">
					{dropdownOptions.map((dropdownItem) => (
						<button
							onClick={(e) => {
								e.stopPropagation()
								setCurrentDropdown('')
								setShowMobileMenu?.(false)
							}}
							key={dropdownItem.id as string}
							className={clsx(
								'w-full text-left hover:bg-white-frosted max-lg:px-3',
								!isResizing && 'transition-colors duration-300'
							)}
						>
							<Cta
								cta={dropdownItem}
								className={clsx(
									'block w-full whitespace-nowrap p-4 px-4 transition-colors lg:hover:bg-gray-100 hover:text-blue-dark lg:py-2'
								)}
							>
								{dropdownItem.label}
							</Cta>
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
